import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { styled, useTheme } from "@monolith-forensics/monolith-ui";
import { useFeatures } from "@/contexts/FeaturesContext";
import { usePermissions } from "@/hooks";
import { CocContent } from "@/Features";
import { EvidencePhotosContent } from "@/Features/EvidencePhotos/EvidencePhotosContent";
import { EvidenceFilesContent } from "@/Features/EvidenceFiles";
import ChildItemsContent from "@/pages/Evidence/ChildItemsContent";
import ProgressBar from "@/Monolith-UI/ProgressBar/ProgressBar";
import { OverviewContent } from "./OverviewContent";
import AuditContent from "@/pages/Evidence/AuditContent";
import AcquisitionView from "@/Features/Acquisitions/AcquisitionView";
import { EvidenceProgressQuery, useEvidenceContext } from "@/Features";
import type { Evidence } from "@/Features/Evidence/evidence.types";
import { EVIDENCE_TAB_SLUGS } from "./evidenceTabSlugs";
import type { EvidenceTabSlug } from "./evidenceTabSlugs";

type TabVisibilityContext = {
  evidenceItem: Evidence | undefined;
  hasAuditRead: boolean;
  filesInCaseFiles: boolean;
};

type TabDescriptor = {
  slug: EvidenceTabSlug;
  label: string;
  isVisible?: (ctx: TabVisibilityContext) => boolean;
  /**
   * Stays mounted (hidden) once opened for this item, like the storage item
   * Photos tab: its upload queue lives in the component, and unmounting it
   * would abort uploads in flight.
   */
  keepMounted?: boolean;
  render: (evidenceItem: Evidence) => ReactNode;
};

const TABS: TabDescriptor[] = [
  {
    slug: EVIDENCE_TAB_SLUGS.OVERVIEW,
    label: "Overview",
    render: () => <OverviewContent />,
  },
  {
    slug: EVIDENCE_TAB_SLUGS.COC,
    label: "Chain of Custody",
    render: (evidenceItem) => <CocContent evidenceInfo={evidenceItem} />,
  },
  {
    slug: EVIDENCE_TAB_SLUGS.ACQUISITIONS,
    label: "Acquisitions",
    render: (evidenceItem) => <AcquisitionView infoData={evidenceItem} />,
  },
  {
    slug: EVIDENCE_TAB_SLUGS.CHILD_ITEMS,
    label: "Child Items",
    isVisible: ({ evidenceItem }) => evidenceItem?.is_parent === 1,
    render: (evidenceItem) => <ChildItemsContent evidenceInfo={evidenceItem} />,
  },
  {
    slug: EVIDENCE_TAB_SLUGS.PHOTOS,
    label: "Photos",
    keepMounted: true,
    render: (evidenceItem) => (
      <EvidencePhotosContent evidenceInfo={evidenceItem} />
    ),
  },
  {
    slug: EVIDENCE_TAB_SLUGS.FILES,
    label: "Files",
    // The item's folder in Case Files, which the Files API hides while the
    // flag is off. The gate goes with ENG-1790.
    isVisible: ({ filesInCaseFiles }) => filesInCaseFiles,
    // Keyed by item so moving to another item starts a fresh table: the
    // listing's placeholderData would otherwise show the last item's rows,
    // with their actions, until the new folder's listing returns.
    render: (evidenceItem) => (
      <EvidenceFilesContent
        key={evidenceItem.evidence_id}
        evidenceInfo={evidenceItem}
      />
    ),
  },
  {
    slug: EVIDENCE_TAB_SLUGS.AUDIT,
    label: "Audit Logs",
    isVisible: ({ hasAuditRead }) => hasAuditRead,
    render: (evidenceItem) => <AuditContent evidenceInfo={evidenceItem} />,
  },
];

const StyledMainContent = styled.div`
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;

  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0px;

  & .toggle-menu {
    margin-bottom: 10px;
    display: flex;
    overflow-x: auto;
  }
  & .header {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 1.1rem;
    font-weight: 500;
  }
  & .description {
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 12px;
    margin-top: 10px;
  }
`;

const MainContent: React.FC<{}> = () => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();
  const { data: evidenceItem } = useEvidenceContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const hasAuditRead = hasPermission(MONOLITH_PERMISSIONS.AUDITS_READ);
  const filesInCaseFiles = hasFeatures([FEATURES.EVIDENCE_FILES_IN_CASE_FILES]);
  const visibleTabs = TABS.filter(
    (tab) =>
      !tab.isVisible ||
      tab.isVisible({ evidenceItem, hasAuditRead, filesInCaseFiles })
  );

  const requestedSlug = searchParams.get("tab") ?? "";
  const activeTab =
    visibleTabs.find((tab) => tab.slug === requestedSlug) ?? visibleTabs[0];
  const activeSlug = activeTab.slug;

  // keepMounted tabs opened for this evidence item. Another item starts over,
  // so its tabs (and their upload queues) mount fresh.
  const evidenceId = evidenceItem?.evidence_id;
  const [visited, setVisited] = useState<{
    evidenceId?: number;
    slugs: EvidenceTabSlug[];
  }>({ slugs: [] });
  const visitedSlugs = useMemo(
    () => (visited.evidenceId === evidenceId ? visited.slugs : []),
    [visited, evidenceId]
  );
  const mountedSlugs = useMemo(
    () =>
      activeTab.keepMounted && !visitedSlugs.includes(activeSlug)
        ? [...visitedSlugs, activeSlug]
        : visitedSlugs,
    [activeTab.keepMounted, activeSlug, visitedSlugs]
  );

  useEffect(() => {
    if (
      mountedSlugs.length !== visitedSlugs.length ||
      visited.evidenceId !== evidenceId
    ) {
      setVisited({ evidenceId, slugs: mountedSlugs });
    }
  }, [mountedSlugs, visitedSlugs.length, visited.evidenceId, evidenceId]);

  useEffect(() => {
    if (!evidenceItem) return;
    if (requestedSlug && requestedSlug !== activeSlug) {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [evidenceItem, requestedSlug, activeSlug, searchParams, setSearchParams]);

  const buttonStyle = {
    minWidth: "150px",
    padding: "2px 5px",
  };

  const handleChange = (_event: unknown, nextSlug: EvidenceTabSlug | null) => {
    if (nextSlug === null) return;
    if (!visibleTabs.some((tab) => tab.slug === nextSlug)) return;
    const next = new URLSearchParams(searchParams);
    if (nextSlug === EVIDENCE_TAB_SLUGS.OVERVIEW) {
      next.delete("tab");
    } else {
      next.set("tab", nextSlug);
    }
    setSearchParams(next, { replace: true });
  };

  if (!evidenceItem) return null;

  return (
    <StyledMainContent>
      <div className="toggle-menu">
        <ToggleButtonGroup
          size="small"
          value={activeSlug}
          onChange={handleChange}
          exclusive
          style={{ margin: "auto", flexWrap: "wrap", justifyContent: "center" }}
        >
          {visibleTabs.map((tab) => (
            <ToggleButton
              key={tab.slug}
              value={tab.slug}
              size="small"
              disableRipple={true}
              style={buttonStyle}
            >
              {tab.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
      {!activeTab.keepMounted && activeTab.render(evidenceItem)}
      {visibleTabs
        .filter((tab) => tab.keepMounted && mountedSlugs.includes(tab.slug))
        .map((tab) => (
          // Keyed on the item so another item gets its own upload queue: the
          // queue reads the item's routes when each upload starts and
          // completes.
          <div
            key={`${tab.slug}:${evidenceItem.evidence_id}`}
            style={{
              display: tab.slug === activeSlug ? "flex" : "none",
              flexDirection: "column",
              flex: "1 1 auto",
              minHeight: 0,
            }}
          >
            {tab.render(evidenceItem)}
          </div>
        ))}
    </StyledMainContent>
  );
};

const StyledEvidenceOverview = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  overflow-y: auto;
  height: 0px;
`;

export const EvidenceOverview: React.FC<{}> = () => {
  const theme = useTheme();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();

  const { data: evidenceItem, update } = useEvidenceContext() || {};

  const { data: evidenceProgress, isLoading } =
    EvidenceProgressQuery.useGetEvidenceProgress();

  const handleProgressUpdate = (item: any) => {
    if (!evidenceItem?.evidence_id) return;
    update?.({
      progress: item.progress_name,
    });
  };

  return (
    <StyledEvidenceOverview>
      <div
        style={{
          margin: "20px 0px",
          padding: "20px",
          paddingTop: "30px",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 5,
        }}
      >
        {!isLoading && (
          <ProgressBar
            defaultValue={evidenceItem?.progress}
            progressItems={evidenceProgress || []}
            onSelect={handleProgressUpdate}
            enabled={hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_UPDATE)}
          />
        )}
      </div>
      <MainContent />
    </StyledEvidenceOverview>
  );
};
